<div class="provider-page">
  <div class="provider-page__container">
    <div class="provider-page__content">
      <!-- Left: Provider Selection Card -->
      <div class="provider-page__main">
        <div class="provider-page__card">
          <!-- Header -->
          <div class="provider-page__card-header">
            <h2 class="provider-page__card-title">{{ 'PROVIDER_SELECTION.TITLE' | translate }}</h2>

            @if (canUseAllMode()) {
              <div class="provider-page__filter-wrapper" #filterWrapper>
                <div
                  class="provider-page__filter"
                  (click)="toggleDropdown()"
                  role="button"
                  tabindex="0"
                  (keydown.enter)="toggleDropdown()"
                  (keydown.space)="toggleDropdown(); $event.preventDefault()"
                >
                  <span class="provider-page__filter-text">{{ modeLabel() | translate }}</span>
                  <mat-icon class="provider-page__filter-icon">arrow_drop_down</mat-icon>
                </div>

                @if (dropdownOpen()) {
                  <div class="provider-page__dropdown" role="listbox">
                    <div
                      class="provider-page__dropdown-option"
                      [class.provider-page__dropdown-option--active]="providerSelectionMode() === 'all'"
                      (click)="setMode('all')"
                      role="option"
                      [attr.aria-selected]="providerSelectionMode() === 'all'"
                    >
                      {{ 'PROVIDER_SELECTION.SHOW_PROVIDER_FOR_ALL_SERVICES' | translate }}
                    </div>
                    <div
                      class="provider-page__dropdown-option"
                      [class.provider-page__dropdown-option--active]="providerSelectionMode() === 'per-service'"
                      (click)="setMode('per-service')"
                      role="option"
                      [attr.aria-selected]="providerSelectionMode() === 'per-service'"
                    >
                      {{ 'PROVIDER_SELECTION.CHOOSE_PROVIDER_PER_SERVICE' | translate }}
                    </div>
                  </div>
                }
              </div>
            }
          </div>

          @if (loading()) {
            <div class="provider-page__loading">
              <div class="provider-page__skeleton-providers">
                @for (i of [1, 2, 3, 4]; track i) {
                  <div class="provider-page__skeleton-provider">
                    <div class="provider-page__skeleton-circle"></div>
                    <div class="provider-page__skeleton-circle provider-page__skeleton-circle--avatar"></div>
                    <div class="provider-page__skeleton-lines">
                      <div class="provider-page__skeleton-line provider-page__skeleton-line--long"></div>
                      <div class="provider-page__skeleton-line provider-page__skeleton-line--short"></div>
                    </div>
                  </div>
                }
              </div>
            </div>
          } @else if (serviceGroups().length === 0) {
            <div class="provider-page__selection-area">
              <div class="provider-page__empty">
                {{ 'PROVIDER_SELECTION.NO_SERVICES_SELECTED' | translate }}
              </div>
            </div>
          } @else if (clientChoiceGroups().length === 0) {
            <div class="provider-page__selection-area">
              <div class="provider-page__empty">
                <h3 class="provider-page__empty-title">
                  {{ 'PROVIDER_SELECTION.PROVIDER_ASSIGNED_TITLE' | translate }}
                </h3>
                <p class="provider-page__empty-text">
                  {{ 'PROVIDER_SELECTION.PROVIDER_ASSIGNED_MESSAGE' | translate }}
                </p>
              </div>
            </div>
          } @else if (canUseAllMode() && providerSelectionMode() === 'all') {
            <!-- MODE: All Services -->
            <div class="provider-page__selection-area">
              <div class="provider-page__inner-card">
                <div class="provider-page__chips">
                  @for (group of clientChoiceGroups(); track group.id) {
                    <span class="provider-page__chip">{{ group.name }}</span>
                  }
                </div>

                <div class="provider-page__duration">{{ totalDurationLabel() }}</div>

                <hr class="provider-page__divider" />

                <div
                  class="provider-page__providers"
                  role="radiogroup"
                  [attr.aria-label]="'PROVIDER_SELECTION.SELECT_PROVIDER_FOR_ALL_ARIA_LABEL' | translate"
                >
                  <!-- Any available -->
                  <div
                    class="provider-page__provider"
                    [class.provider-page__provider--selected]="allSelectedProviderId() === 'any'"
                    (click)="selectProviderForAll('any')"
                    role="radio"
                    [attr.aria-checked]="allSelectedProviderId() === 'any'"
                    tabindex="0"
                    (keydown.enter)="selectProviderForAll('any')"
                    (keydown.space)="selectProviderForAll('any'); $event.preventDefault()"
                  >
                    <div class="provider-page__provider-radio">
                      <div
                        class="provider-page__radio-outer"
                        [class.provider-page__radio-outer--selected]="allSelectedProviderId() === 'any'"
                      >
                        @if (allSelectedProviderId() === 'any') {
                          <div class="provider-page__radio-inner"></div>
                        }
                      </div>
                    </div>
                    <div class="provider-page__provider-avatar">
                      <mat-icon>shuffle</mat-icon>
                    </div>
                    <div class="provider-page__provider-info">
                      <span class="provider-page__provider-name">{{
                        'PROVIDER_SELECTION.ANY_AVAILABLE' | translate
                      }}</span>
                      <span class="provider-page__provider-subtitle">{{
                        'PROVIDER_SELECTION.BASED_ON_AVAILABILITY' | translate
                      }}</span>
                    </div>
                  </div>

                  @for (provider of commonProviders(); track provider.id) {
                    <div
                      class="provider-page__provider"
                      [class.provider-page__provider--selected]="allSelectedProviderId() === provider.id"
                      (click)="selectProviderForAll(provider.id)"
                      role="radio"
                      [attr.aria-checked]="allSelectedProviderId() === provider.id"
                      tabindex="0"
                      (keydown.enter)="selectProviderForAll(provider.id)"
                      (keydown.space)="selectProviderForAll(provider.id); $event.preventDefault()"
                    >
                      <div class="provider-page__provider-radio">
                        <div
                          class="provider-page__radio-outer"
                          [class.provider-page__radio-outer--selected]="allSelectedProviderId() === provider.id"
                        >
                          @if (allSelectedProviderId() === provider.id) {
                            <div class="provider-page__radio-inner"></div>
                          }
                        </div>
                      </div>
                      <div class="provider-page__provider-avatar">
                        <span class="provider-page__provider-initials">{{ provider.initial }}</span>
                      </div>
                      <div class="provider-page__provider-info">
                        <span class="provider-page__provider-name">{{ provider.name }}</span>
                        @if (provider.subtitle) {
                          <span class="provider-page__provider-subtitle">{{ provider.subtitle }}</span>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          } @else {
            <!-- MODE: Per Service -->
            <div class="provider-page__selection-area">
              @for (group of clientChoiceGroups(); track group.id; let idx = $index) {
                <div class="provider-page__service-section">
                  <div
                    class="provider-page__service-header"
                    (click)="toggleServiceExpanded(idx)"
                    role="button"
                    tabindex="0"
                    [attr.aria-expanded]="group.expanded"
                    (keydown.enter)="toggleServiceExpanded(idx)"
                    (keydown.space)="toggleServiceExpanded(idx); $event.preventDefault()"
                  >
                    <div class="provider-page__service-header-text">
                      <span class="provider-page__service-name">{{ group.name }}</span>
                      @if (group.description) {
                        <span class="provider-page__service-description">{{ group.description }}</span>
                      }
                    </div>
                    <mat-icon
                      class="provider-page__service-toggle"
                      [class.provider-page__service-toggle--expanded]="group.expanded"
                    >
                      expand_more
                    </mat-icon>
                  </div>

                  @if (group.expanded) {
                    <div class="provider-page__service-body">
                      @if (group.providers.length === 0) {
                        <!-- No provider data from API -->
                      } @else if (group.providers.length === 1) {
                        <!-- Single provider — selected, non-interactive -->
                        <div class="provider-page__providers">
                          <div
                            class="provider-page__provider provider-page__provider--selected provider-page__provider--readonly"
                          >
                            <div class="provider-page__provider-radio">
                              <div class="provider-page__radio-outer provider-page__radio-outer--selected">
                                <div class="provider-page__radio-inner"></div>
                              </div>
                            </div>
                            <div class="provider-page__provider-avatar">
                              <span class="provider-page__provider-initials">{{ group.providers[0].initial }}</span>
                            </div>
                            <div class="provider-page__provider-info">
                              <span class="provider-page__provider-name">{{ group.providers[0].name }}</span>
                              @if (group.providers[0].subtitle) {
                                <span class="provider-page__provider-subtitle">{{ group.providers[0].subtitle }}</span>
                              }
                            </div>
                          </div>
                        </div>
                      } @else if (group.meetingType === TeamEventMeetingType.ROUND_ROBIN) {
                        <!-- ROUND_ROBIN with multiple providers — only "Any available", no individual host selection -->
                        <div class="provider-page__providers">
                          <div
                            class="provider-page__provider provider-page__provider--selected provider-page__provider--readonly"
                          >
                            <div class="provider-page__provider-radio">
                              <div class="provider-page__radio-outer provider-page__radio-outer--selected">
                                <div class="provider-page__radio-inner"></div>
                              </div>
                            </div>
                            <div class="provider-page__provider-avatar">
                              <mat-icon>shuffle</mat-icon>
                            </div>
                            <div class="provider-page__provider-info">
                              <span class="provider-page__provider-name">{{
                                'PROVIDER_SELECTION.ANY_AVAILABLE' | translate
                              }}</span>
                              <span class="provider-page__provider-subtitle">{{
                                'PROVIDER_SELECTION.BASED_ON_AVAILABILITY' | translate
                              }}</span>
                            </div>
                          </div>
                        </div>
                      } @else if (group.meetingType === TeamEventMeetingType.MULTI_HOST) {
                        <!-- MULTI_HOST — all providers selected, non-interactive -->
                        <div class="provider-page__providers">
                          @for (provider of group.providers; track provider.id) {
                            <div
                              class="provider-page__provider provider-page__provider--selected provider-page__provider--readonly"
                            >
                              <div class="provider-page__provider-radio">
                                <div class="provider-page__radio-outer provider-page__radio-outer--selected">
                                  <div class="provider-page__radio-inner"></div>
                                </div>
                              </div>
                              <div class="provider-page__provider-avatar">
                                <span class="provider-page__provider-initials">{{ provider.initial }}</span>
                              </div>
                              <div class="provider-page__provider-info">
                                <span class="provider-page__provider-name">{{ provider.name }}</span>
                                @if (provider.subtitle) {
                                  <span class="provider-page__provider-subtitle">{{ provider.subtitle }}</span>
                                }
                              </div>
                            </div>
                          }
                        </div>
                      } @else {
                        <!-- Multiple providers: "Any available" + selectable grid -->
                        <div
                          class="provider-page__providers"
                          role="radiogroup"
                          [attr.aria-label]="
                            'PROVIDER_SELECTION.SELECT_PROVIDER_FOR_SERVICE_ARIA_LABEL'
                              | translate: { serviceName: group.name }
                          "
                        >
                          <div
                            class="provider-page__provider"
                            [class.provider-page__provider--selected]="group.selectedProviderId === 'any'"
                            (click)="selectProviderForService(group.id, 'any')"
                            role="radio"
                            [attr.aria-checked]="group.selectedProviderId === 'any'"
                            tabindex="0"
                            (keydown.enter)="selectProviderForService(group.id, 'any')"
                            (keydown.space)="selectProviderForService(group.id, 'any'); $event.preventDefault()"
                          >
                            <div class="provider-page__provider-radio">
                              <div
                                class="provider-page__radio-outer"
                                [class.provider-page__radio-outer--selected]="group.selectedProviderId === 'any'"
                              >
                                @if (group.selectedProviderId === 'any') {
                                  <div class="provider-page__radio-inner"></div>
                                }
                              </div>
                            </div>
                            <div class="provider-page__provider-avatar">
                              <mat-icon>shuffle</mat-icon>
                            </div>
                            <div class="provider-page__provider-info">
                              <span class="provider-page__provider-name">{{
                                'PROVIDER_SELECTION.ANY_AVAILABLE' | translate
                              }}</span>
                              <span class="provider-page__provider-subtitle">{{
                                'PROVIDER_SELECTION.BASED_ON_AVAILABILITY' | translate
                              }}</span>
                            </div>
                          </div>

                          @for (provider of group.providers; track provider.id) {
                            <div
                              class="provider-page__provider"
                              [class.provider-page__provider--selected]="group.selectedProviderId === provider.id"
                              (click)="selectProviderForService(group.id, provider.id)"
                              role="radio"
                              [attr.aria-checked]="group.selectedProviderId === provider.id"
                              tabindex="0"
                              (keydown.enter)="selectProviderForService(group.id, provider.id)"
                              (keydown.space)="selectProviderForService(group.id, provider.id); $event.preventDefault()"
                            >
                              <div class="provider-page__provider-radio">
                                <div
                                  class="provider-page__radio-outer"
                                  [class.provider-page__radio-outer--selected]="
                                    group.selectedProviderId === provider.id
                                  "
                                >
                                  @if (group.selectedProviderId === provider.id) {
                                    <div class="provider-page__radio-inner"></div>
                                  }
                                </div>
                              </div>
                              <div class="provider-page__provider-avatar">
                                <span class="provider-page__provider-initials">{{ provider.initial }}</span>
                              </div>
                              <div class="provider-page__provider-info">
                                <span class="provider-page__provider-name">{{ provider.name }}</span>
                                @if (provider.subtitle) {
                                  <span class="provider-page__provider-subtitle">{{ provider.subtitle }}</span>
                                }
                              </div>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- Right: Cart Sidebar -->
      <div class="provider-page__cart-wrapper">
        <app-booking-cart
          [items]="cartItems()"
          [ctaLabel]="'PROVIDER_SELECTION.CHOOSE_DATE_AND_TIME' | translate"
          [ctaDisabled]="ctaDisabled()"
          [showBackButton]="!isSingleEventRoute()"
          [readOnly]="isSingleEventRoute()"
          [backLabel]="'BOOK.BACK' | translate"
          (ctaClick)="onChooseDateAndTime()"
          (backClick)="onBack()"
          (removeItem)="onRemoveCartItem($event)"
        ></app-booking-cart>
      </div>
    </div>
  </div>
</div>
